import { useCallback, useState } from 'react';
import { PIN_INTENT_WINDOW, PIN_INTENT_RESEQUENCE } from './PinStopModal';

// Owns the PinStopModal lifecycle for a single RouteCard. Both call paths
// (drag-drop and per-row pin / window-cell click) share the same opener;
// they differ only in the tagged `intent` that switches the modal's title,
// body copy, and submit button label without changing the submitted shape.
//
// Prefill is sourced from the stop's own effective window — anchor first,
// then the value already on the row (supervisor edit / NextMV input window
// (`nextmvDeliveryWindowStart/End`) / optimizer fallback). Stops with
// neither pass `prefill: null` so the
// modal opens with its placeholder.
//
// `edits` is the `useRouteEdits` return — the hook reads `anchoredStops`
// and writes via `setAnchor` on confirm.
export default function usePinModal(edits) {
  const [pendingPin, setPendingPin] = useState(null);

  const openPinForStop = useCallback(
    (stop, stopKey, intent) => {
      const anchor = edits.anchoredStops.get(stopKey);
      const startTimeWindow =
        (anchor && anchor.startTimeWindow) || stop.startTimeWindow || '';
      const endTimeWindow =
        (anchor && anchor.endTimeWindow) || stop.endTimeWindow || '';
      setPendingPin({
        stop,
        stopKey,
        intent,
        prefill:
          startTimeWindow || endTimeWindow
            ? { startTimeWindow, endTimeWindow }
            : null,
      });
    },
    [edits.anchoredStops],
  );

  // Drag-drop intent: the supervisor moved the row to a new position;
  // window stays as-is unless they choose to adjust it in the modal.
  // `neighborKey` is intentionally unused — the drop position is
  // communicated by the new sequence Nextmv produces, not the anchor.
  const requestPinFromDrop = useCallback(
    ({ stop, stopKey }) => {
      openPinForStop(stop, stopKey, PIN_INTENT_RESEQUENCE);
    },
    [openPinForStop],
  );

  // Per-row Pin button or window-cell click: the supervisor explicitly
  // wants to set / change the window.
  const requestPinFromRow = useCallback(
    (stop, stopKey) => {
      openPinForStop(stop, stopKey, PIN_INTENT_WINDOW);
    },
    [openPinForStop],
  );

  const submitPin = useCallback(
    (anchor) => {
      if (!pendingPin) return;
      edits.setAnchor(pendingPin.stopKey, anchor);
      setPendingPin(null);
    },
    [pendingPin, edits],
  );

  const cancelPin = useCallback(() => setPendingPin(null), []);

  return {
    pendingPin,
    requestPinFromDrop,
    requestPinFromRow,
    submitPin,
    cancelPin,
  };
}
